import { castArray } from 'lodash';
import NextRouter from 'next/router';
import {
  type InteractionContact,
  InteractionType,
} from '@/common/types/interaction';
import { formatAddress } from '@/common/utils/address';
import { formatDisplayName } from '@/common/utils/formatDisplayName';
import { coreApi } from '$api-clients';
import type { CoreRequest } from '$api-clients/hooks';

const interactionTypeLabels: Partial<Record<InteractionType, string>> = {
  [InteractionType.lease]: 'a Lease',
  [InteractionType.listing]: 'a Listing',
  [InteractionType.offer]: 'an Offer',
};

type InteractionResult = CoreRequest<'/interactions/{interactionId}'>['Data'];

export const getDescriptionFromPath = (
  path: string,
  type: InteractionType,
): string | null => {
  const typeLabel = interactionTypeLabels[type];

  switch (true) {
    case /\/(?:offers|listings|leases)\/.*\/cover-sheet/.test(path):
      return `You were working on the Cover Sheet for ${typeLabel}`;
    case /\/(?:offers|listings|leases)\/.*\/documents/.test(path):
      return `You were working on Documents for ${typeLabel}`;
    case /\/(?:listings|leases)\/.*\/offers/.test(path):
      return `You were working on Offer Management for ${typeLabel}`;
    case /\/(?:offers|listings|leases)\/.*\/timeline/.test(path):
      return `You were working on the Closing Timeline for ${typeLabel}`;
    case /\/(?:offers|leases)\/.*\/cover-letter/.test(path):
      return `You were working on the Cover Letter for ${typeLabel}`;
    case /\/(?:offers|leases)\/.*\/pre-qual/.test(path):
      return `You were working on the Pre Qual for ${typeLabel}`;
    case /\/(?:offers|leases)\/.*\/disclosures/.test(path):
      return `You were working on Signed Disclosures for ${typeLabel}`;
    case /\/(?:offers|leases)\/.*\/proof-of-funds/.test(path):
      return `You were working on Proof of Funds for ${typeLabel}`;
    default:
      return null;
  }
};

const getInteractionAddress = (data?: InteractionResult) => {
  if (!data?.interaction) return null;

  if ('property' in data.interaction) {
    return formatAddress(data.interaction.property, { includeStreet2: true });
  }

  if ('listing' in data.interaction && data.interaction.listing?.property) {
    return formatAddress(data.interaction.listing.property, {
      includeStreet2: true,
    });
  }

  return null;
};

const getReferralDescription = (
  interactionData?: InteractionResult,
  contacts?: InteractionContact[],
) => {
  if (
    interactionData?.type !== 'referral' ||
    !('referralType' in interactionData.interaction)
  ) {
    return null;
  }

  const primaryAgent = contacts?.find((contact) => contact.type === 'agent');

  const counterpartyAgent = contacts?.find(
    (contact) => contact.type === 'counterpartyAgent',
  );

  const agent =
    interactionData.interaction.referralType === 'receiving'
      ? counterpartyAgent
      : primaryAgent;

  const agentDisplayName = formatDisplayName({
    firstName: agent?.firstName,
    lastName: agent?.lastName,
  });

  if (!agentDisplayName) {
    return null;
  }

  const referralText =
    interactionData.interaction.referralType === 'receiving' ? 'from' : 'to';

  return `A referral ${referralText} ${agentDisplayName}`;
};

export const trackReferral = async ({
  path,
  interactionId,
}: {
  path: string;
  interactionId: string;
}) => {
  const [interactionResult, contactsResult] = await Promise.all([
    coreApi.GET('/interactions/{interactionId}', {
      params: {
        path: {
          interactionId,
        },
      },
    }),
    coreApi.GET('/interactions/{interactionId}/contacts', {
      params: {
        path: {
          interactionId,
        },
      },
    }),
  ]);

  const description = getReferralDescription(
    interactionResult.data,
    contactsResult.data,
  );
  if (!description) return;

  await coreApi.PUT('/users/me/location-history', {
    body: {
      description,
      url: path,
      title: 'You were working on a Referral',
    },
  });
};

const getFormattedBuyerName = (data?: InteractionContact[]) => {
  const client = data?.find((contact) => contact.type === 'client');

  return formatDisplayName({
    firstName: client?.firstName,
    lastName: client?.lastName,
  });
};

export const trackReport = async ({
  url,
  reportName,
}: {
  url: string;
  reportName: string;
}) => {
  await coreApi.PUT('/users/me/location-history', {
    body: {
      description: '',
      url,
      title: `You were viewing the ${reportName} Report`,
    },
  });
};

export const trackClientGroup = async ({
  path,
  interactionId,
}: {
  path: string;
  interactionId: string;
}) => {
  const contactsResult = await coreApi.GET(
    '/interactions/{interactionId}/contacts',
    {
      params: {
        path: {
          interactionId,
        },
      },
    },
  );

  const description = getFormattedBuyerName(contactsResult.data);
  if (!description) return;

  await coreApi.PUT('/users/me/location-history', {
    body: {
      description,
      url: path,
      title: 'You were working on a Buyer Agreement',
    },
  });
};

export const trackPropertyInteraction = async ({
  path,
  interactionId,
  interactionType,
  pathname,
}: {
  path: string;
  interactionId: string;
  interactionType: InteractionType;
  pathname: string;
}) => {
  const title = getDescriptionFromPath(pathname, interactionType);
  if (!title) return;

  const interactionData = await coreApi.GET('/interactions/{interactionId}', {
    params: {
      path: {
        interactionId,
      },
    },
  });

  const address = getInteractionAddress(interactionData.data);
  if (!address) return;

  await coreApi.PUT('/users/me/location-history', {
    body: {
      description: address,
      url: path,
      title,
    },
  });
};

export const getInteractionInformation = () => {
  const {
    query: { listingId, offerId, leaseId, referralId, clientGroupId },
  } = NextRouter;

  let interactionType: InteractionType | undefined;
  if (listingId) interactionType = InteractionType.listing;
  if (offerId) interactionType = InteractionType.offer;
  if (leaseId) interactionType = InteractionType.lease;
  if (referralId) interactionType = InteractionType.referral;
  if (clientGroupId) interactionType = InteractionType.clientGroup;

  return {
    interactionId: castArray(
      listingId ?? offerId ?? leaseId ?? referralId ?? clientGroupId ?? null,
    )[0],
    interactionType,
  };
};

export const getRouteFromPathname = (path: string) => {
  // Track documents page when in document viewer/tagger
  const match = /(.+\/documents)\/[^/?]+/.exec(path);
  if (match) {
    return match[1];
  }

  return path;
};
